import type { Accessor, ParentComponent, Setter } from "solid-js";
import { createContext, createMemo, createSignal } from "solid-js";
import { initializedContext } from "../../../utils/initializedUtil";
import type { ColumnDefWithId } from "./Table";
import { isEmpty } from "lodash-es";
import type { FilterStore } from "../../../utils/viewConfigurationStore";
import type { RowData, RowSelectionState } from "@tanstack/solid-table";

type TableContextValue = {
  filterStore: Accessor<FilterStore | undefined>;
  setFilterStore: Setter<FilterStore | undefined>;
  rowSelection: Accessor<RowSelectionState>;
  columnVisibility: Accessor<string[]>;
  filterableColumns: Accessor<ColumnDefWithId<RowData>[]>;
  totalCount: Accessor<number>;
  isEmpty: Accessor<boolean>;
  isInitialized: Accessor<boolean>;
  setRowSelection: Setter<RowSelectionState>;
  setColumnVisibility: (ids: string[]) => void;
  setFilterableColumns: (columns: ColumnDefWithId<RowData>[]) => void;
  setTotalCount: (count: number) => void;
  triggerCreateFilter: (columnId: string) => void;
  setCreateFilterHandler: (fn: (columnId: string) => void) => void;
};

const TableContext = createContext<TableContextValue>();

export const TableProvider: ParentComponent = (props) => {
  const [filterStore, setFilterStore] = createSignal<FilterStore>();
  const [rowSelection, setRowSelection] = createSignal<RowSelectionState>({});
  const [columnVisibility, setColumnVisibility] = createSignal<string[]>([]);
  const [filterableColumns, setFilterableColumns] = createSignal<ColumnDefWithId<RowData>[]>([]);
  const [totalCount, setTotalCount] = createSignal(-1); // -1 = not yet initialized
  const [createFilterHandler, setCreateFilterHandlerSignal] = createSignal<(columnId: string) => void>(() => undefined);

  const isTableEmpty = createMemo(() => isEmpty(filterStore()?.all()) && totalCount() === 0);
  const isInitialized = createMemo(() => totalCount() >= 0);

  const value: TableContextValue = {
    filterStore,
    setFilterStore,
    rowSelection,
    columnVisibility,
    filterableColumns,
    totalCount,
    isEmpty: isTableEmpty,
    isInitialized,
    setRowSelection,
    setColumnVisibility,
    setFilterableColumns: (columns) => setFilterableColumns(columns),
    setTotalCount,
    triggerCreateFilter: (columnId) => createFilterHandler()(columnId),
    setCreateFilterHandler: (fn) => setCreateFilterHandlerSignal(() => fn),
  };

  return <TableContext.Provider value={value}>{props.children}</TableContext.Provider>;
};

export const useTable = () => initializedContext(TableContext, "useTableContext", "TableProvider");
